import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, Input, Empty, Dropdown, Menu, Button, Tag, Checkbox } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";

const { Search } = Input;

export interface CompsListItemData {
  id: string;
  /** 纯字符串或自定义节点；非字符串时请提供 searchText 供搜索过滤 */
  leftContent: string | React.ReactNode;
  /** 搜索匹配用：优先此项；不传且 leftContent 为字符串时回退到 leftContent */
  searchText?: string;
  rightContent?: React.ReactNode;
  [key: string]: any;
}

/** 行首复选框（如角色成员多选） */
export interface CompsListRowSelection {
  selectedRowKeys: React.Key[];
  onChange: (keys: React.Key[]) => void;
  getCheckboxProps?: (item: CompsListItemData) => { disabled?: boolean };
  /** 在搜索框与列表之间展示「全选」，仅作用于当前筛选后的行 */
  showSelectAll?: boolean;
  /** 全选旁文案，默认「全选」 */
  selectAllLabel?: React.ReactNode;
}

export interface CompsListProps {
  listData: CompsListItemData[];
  loading?: boolean;
  onSelect?: (item: CompsListItemData) => void;
  showSearch?: boolean;
  selectedId?: string;
  /** 行首显示复选框，与 selectedId 独立 */
  rowSelection?: CompsListRowSelection;
  menuItems?: { key: string; label: string }[];
  onMenuClick?: (item: CompsListItemData, action: string) => void;
  height?: number | string;
  style?: React.CSSProperties;
  className?: string;
  searchPlaceholder?: string;
  defaultSearchValue?: string;
  draggable?: boolean; // 是否支持拖拽
  onDragStart?: (item: CompsListItemData, event: React.DragEvent) => void; // 拖拽开始回调
  /**
   * 当列表顺序发生变化时回调（仅在 draggable 且未开启搜索时触发）
   * 由上层负责根据新的顺序更新外部数据源并持久化（如调用后端接口）
   */
  onReorder?: (newList: CompsListItemData[]) => void;
  /** 每行主 flex 的 alignItems，多行主文案时可设为 flex-start */
  itemRowAlignItems?: "center" | "flex-start";
}

/** 点击是否来自行内可交互控件（复选框、按钮等），若是则不应触发行 onSelect */
function isClickFromRowInteractiveControl(target: EventTarget | null): boolean {
  if (target == null || !(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      [
        "input",
        "textarea",
        "select",
        "button",
        'a[href]',
        '[role="checkbox"]',
        '[role="switch"]',
        ".ant-checkbox",
        ".ant-checkbox-wrapper",
        ".ant-checkbox-input",
        ".ant-switch",
        ".ant-radio",
        ".ant-radio-wrapper",
        ".list-item-menu-button",
      ].join(",")
    )
  );
}

const extractNumberFromContent = (content: React.ReactNode): string => {
  if (typeof content === "number") return content.toString();
  if (typeof content === "string") return content;
  if (React.isValidElement(content)) {
    const children = (content as any).props?.children;
    if (children) {
      if (typeof children === "string") {
        const match = children.match(/(\d+)/);
        return match ? match[1] : "0";
      }
      if (typeof children === "number") {
        return children.toString();
      }
    }
  }
  return "0";
};

const CompsList: React.FC<CompsListProps> = ({
  listData = [],
  loading = false,
  onSelect,
  showSearch = true,
  selectedId,
  rowSelection,
  menuItems = [],
  onMenuClick,
  height = "100%",
  style,
  className,
  searchPlaceholder = "搜索...",
  defaultSearchValue = "",
  draggable = false,
  onDragStart,
  onReorder,
  itemRowAlignItems = "center",
}) => {
  const [searchValue, setSearchValue] = useState<string>(defaultSearchValue);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const filteredListData = useMemo(() => {
    if (!searchValue) return listData;
    const lower = searchValue.toLowerCase();
    return listData.filter((item) => {
      const raw =
        typeof item.searchText === "string"
          ? item.searchText
          : typeof item.leftContent === "string"
            ? item.leftContent
            : "";
      return raw.toLowerCase().includes(lower);
    });
  }, [listData, searchValue]);

  /** 与行 id 比较时统一为字符串，避免 selectedKeys 为 string、item.id 为 number 时 includes 恒为 false */
  const selectedRowKeySet = useMemo(
    () => new Set((rowSelection?.selectedRowKeys ?? []).map((k) => String(k))),
    [rowSelection?.selectedRowKeys],
  );

  const filteredIdStrings = useMemo(
    () => filteredListData.map((i) => String(i.id)),
    [filteredListData],
  );

  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;

  const applySelectAllFiltered = useCallback((checked: boolean): void => {
    const rs = rowSelectionRef.current;
    if (!rs?.onChange) return;
    const currentKeys = rs.selectedRowKeys ?? [];
    if (checked) {
      const merged = new Set([
        ...currentKeys.map((k) => String(k)),
        ...filteredIdStrings,
      ]);
      rs.onChange([...merged]);
      return;
    }
    const filteredSet = new Set(filteredIdStrings);
    rs.onChange(currentKeys.filter((k) => !filteredSet.has(String(k))));
  }, [filteredIdStrings]);

  useEffect(() => {
    // 当上游 listData 变化时，保持当前搜索逻辑
  }, [listData]);

  const renderItemMenu = (item: CompsListItemData): React.ReactNode => {
    if (!onMenuClick || !menuItems.length) return null;
    const menu = (
      <Menu
        onClick={({ key }): void => {
          onMenuClick && onMenuClick(item, key as string);
        }}
      >
        {menuItems.map((menuItem) => (
          <Menu.Item key={menuItem.key}>{menuItem.label}</Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={["click"]}>
        <Button
          type="text"
          icon={<EllipsisOutlined />}
          size="small"
          onClick={(e): void => {
            e.stopPropagation();
          }}
          style={{
            marginLeft: "auto",
            padding: "0 5px",
            color: "#bfbfbf",
          }}
          className="list-item-menu-button"
        />
      </Dropdown>
    );
  };

  const fillFlexParent = height === "100%";
  return (
    <div
      className={className}
      style={{
        height: typeof height === "number" ? `${height}px` : height,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        ...(fillFlexParent ? { flex: 1, overflow: "hidden" } : {}),
        ...style,
      }}
    >
      {showSearch && (
        <Search
          placeholder={searchPlaceholder}
          allowClear
          value={searchValue}
          onChange={(e): void => setSearchValue(e.target.value)}
          style={{ marginBottom: 16 }}
        />
      )}
      {rowSelection?.showSelectAll && filteredListData.length > 0 ? (
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            flexShrink: 0,
          }}
          onMouseDown={(e): void => {
            e.stopPropagation();
          }}
          onClick={(e): void => {
            e.stopPropagation();
          }}
        >
          {(() => {
            const n = filteredIdStrings.filter((id) => selectedRowKeySet.has(id)).length;
            const allFilteredSelected =
              filteredIdStrings.length > 0 &&
              filteredIdStrings.every((id) => selectedRowKeySet.has(id));
            const someFilteredSelected = n > 0 && n < filteredIdStrings.length;
            return (
              <>
                <Checkbox
                  indeterminate={someFilteredSelected}
                  checked={allFilteredSelected}
                  onChange={(e): void => {
                    applySelectAllFiltered(e.target.checked);
                  }}
                >
                  {rowSelection.selectAllLabel ?? "全选"}
                </Checkbox>
                {someFilteredSelected ? (
                  <span style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                    已选 {n} / {filteredIdStrings.length}
                  </span>
                ) : null}
              </>
            );
          })()}
        </div>
      ) : null}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {filteredListData.length > 0 ? (
          <List
            className="comps-list"
            dataSource={filteredListData}
            /**
             * 带 rowSelection 时：有行仍传 loading 会让 antd List 叠 Spin 层，常拦截行首复选点击。
             * 无 rowSelection 的列表保持原 loading 语义（如刷新时整表转圈）。
             */
            loading={rowSelection ? loading && filteredListData.length === 0 : loading}
            renderItem={(item): React.ReactElement => (
              <List.Item
                key={item.id}
                onClick={(e): void => {
                  if (!onSelect) return;
                  if (isClickFromRowInteractiveControl(e.target)) return;
                  onSelect(item);
                }}
                draggable={draggable}
                onDragStart={(e: React.DragEvent): void => {
                  if (draggable && onDragStart) {
                    setDraggingId(item.id);
                    onDragStart(item, e);
                  } else if (draggable) {
                    // 默认处理：将数据存储到 dataTransfer
                    setDraggingId(item.id);
                    e.dataTransfer.setData("text/plain", item.id);
                    e.dataTransfer.effectAllowed = "move";
                  }
                }}
                onDragOver={(e: React.DragEvent): void => {
                  if (!draggable) return;
                  // 仅在未开启搜索时允许拖拽排序，避免过滤列表导致索引错乱
                  if (searchValue) return;
                  e.preventDefault();
                }}
                onDrop={(e: React.DragEvent): void => {
                  if (!draggable || !onReorder) return;
                  if (searchValue) return;
                  e.preventDefault();
                  const sourceId = draggingId || e.dataTransfer.getData("text/plain");
                  if (!sourceId || sourceId === item.id) return;

                  const sourceIndex = listData.findIndex((it) => it.id === sourceId);
                  const targetIndex = listData.findIndex((it) => it.id === item.id);
                  if (sourceIndex === -1 || targetIndex === -1) return;

                  const newList = [...listData];
                  const [moved] = newList.splice(sourceIndex, 1);
                  newList.splice(targetIndex, 0, moved);

                  onReorder(newList);
                  setDraggingId(null);
                }}
                style={{
                  cursor: draggable ? "grab" : "default",
                  backgroundColor:
                    selectedId === item.id ? "#f0f7ff" : "transparent",
                  padding: "8px",
                  borderRadius: "4px",
                  transition: "background-color 0.3s",
                }}
                className="comps-list-item"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: itemRowAlignItems,
                  }}
                >
                  {rowSelection ? (
                    <span
                      className="comps-list-row-selection-wrap"
                      style={{
                        marginRight: 10,
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: itemRowAlignItems,
                        position: "relative",
                        zIndex: 1,
                      }}
                      onMouseDown={(e): void => {
                        /** 阻止冒泡到 List.Item / Spin，避免外层拦截指针事件 */
                        e.stopPropagation();
                      }}
                      onClick={(e): void => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={selectedRowKeySet.has(String(item.id))}
                        disabled={rowSelection.getCheckboxProps?.(item)?.disabled}
                        onChange={(): void => {
                          const rs = rowSelectionRef.current;
                          if (!rs?.onChange) return;
                          if (rs.getCheckboxProps?.(item)?.disabled) return;
                          const rawKeys = rs.selectedRowKeys ?? [];
                          const idStr = String(item.id);
                          const wasOn = rawKeys.some((k) => String(k) === idStr);
                          const next = wasOn
                            ? rawKeys.filter((k) => String(k) !== idStr)
                            : [...rawKeys, item.id];
                          rs.onChange(next);
                        }}
                      />
                    </span>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      alignItems: itemRowAlignItems,
                      fontWeight: selectedId === item.id ? 500 : 400,
                      justifyContent: "space-between",
                      flex: 1,
                      minWidth: 0,
                      cursor: onSelect ? "pointer" : "default",
                    }}
                    onClick={
                      onSelect
                        ? (): void => {
                            onSelect(item);
                          }
                        : undefined
                    }
                    role={onSelect ? "button" : undefined}
                  >
                    <div style={{ marginRight: 8, minWidth: 0, flex: 1 }}>{item.leftContent}</div>
                    {item.rightContent && (
                      <>
                        {/* 如果是 React 元素（如 Tag），直接渲染以保持样式统一；否则按数字/字符串处理 */}
                        {React.isValidElement(item.rightContent) ? (
                          item.rightContent
                        ) : (() => {
                          const extractedNumber = extractNumberFromContent(item.rightContent);
                          const isNumber = typeof item.rightContent === "number" || 
                                         (typeof item.rightContent === "string" && /^\d+$/.test(item.rightContent)) ||
                                         (extractedNumber !== "0" && extractedNumber !== "");
                          
                          if (isNumber) {
                            return (
                              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                                {extractedNumber}
                              </Tag>
                            );
                          } else {
                            return item.rightContent;
                          }
                        })()}
                      </>
                    )}
                  </div>
                  {renderItemMenu(item)}
                </div>
              </List.Item>
            )}
          />
        ) : (
          <Empty description="暂无数据" />
        )}
      </div>
    </div>
  );
};

export default CompsList;


